import { StepIconProps } from '@mui/material/StepIcon';
import { Dispatch, SetStateAction } from 'react';
import {
  FieldErrors,
  FieldValues,
  Path,
  UseFormTrigger,
} from 'react-hook-form';
import { ZodObject, ZodRawShape, ZodSchema, ZodTypeAny } from 'zod';
import { getSchemaKeys, unwrapZod } from './simple-form-page';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepConnector from '@mui/material/StepConnector';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CheckCircle from '@mui/icons-material/CheckCircle';

export interface ActionTypeProps {
  id: string;
  label: string;
}

export interface FormSubSteps {
  stepIndex: string;
  title: string;
  description?: string;
  hasError: boolean;
}

export interface ActionErrorEntry {
  id: string;
}

export interface StepConfig {
  id?: string;
  label: string;
  schema?: ZodSchema;
  description?: string;
  actionTypes?: ActionTypeProps[];
}

interface SharedFormStepperNewProps<T extends FieldValues> {
  activeStep: number;
  setActiveStep: (step: number) => void;
  steps: StepConfig[];
  errors: FieldErrors<T>;
  trigger: UseFormTrigger<T> | UseFormTrigger<Partial<T>>;
  stepCompleted: boolean[];
  setStepCompleted: Dispatch<SetStateAction<boolean[]>>;
  handleStepClick?: (index: number) => void;
  handleValidation?: () => boolean;
  subStep?: FormSubSteps[];
}

export function stepHasError(
  schema: ZodObject<ZodRawShape>,
  errors: FieldErrors
): boolean {
  const schemaKeys = schema.shape ? Object.keys(schema.shape) : [];

  if (!schemaKeys || schemaKeys.length <= 0) return false;

  return schemaKeys.some((key) => {
    const schemaValue = schema.shape[key] as ZodTypeAny;
    const unwrappedSchema = unwrapZod(schemaValue);

    if (unwrappedSchema instanceof ZodObject) {
      if (
        errors[key] &&
        stepHasError(
          unwrappedSchema as ZodObject<ZodRawShape>,
          errors[key] as FieldErrors
        )
      ) {
        return true;
      }
    } else if (errors[key]) {
      return true;
    }

    return false;
  });
}

function CustomStepIcon({ active, completed, error }: StepIconProps) {
  const theme = useTheme();

  const color = error
    ? theme.palette.semantic.stepper.warning
    : completed
    ? theme.palette.semantic.icon.success
    : active
    ? theme.palette.semantic.stepper.active
    : theme.palette.semantic.stepper.background;

  if (completed && !error) {
    return (
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          backgroundColor: theme.palette.semantic.surface.bg3,
          color: color,
        }}
      >
        <CheckCircle sx={{ fontWeight: 700 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: color,
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    />
  );
}

export default function SharedFormStepperNew<T extends FieldValues>({
  activeStep,
  setActiveStep,
  steps,
  errors,
  trigger,
  stepCompleted,
  setStepCompleted,
  handleStepClick,
  subStep,
}: SharedFormStepperNewProps<T>) {
  const theme = useTheme();

  async function onStepClick(stepIndex: number) {
    if (stepIndex === activeStep || !stepCompleted[stepIndex]) return;

    if (steps[activeStep].schema) {
      const schema = steps[activeStep].schema;

      const fields = getSchemaKeys(schema as ZodObject<ZodRawShape>);

      const isValid = await trigger(
        fields as readonly Path<T>[] & Path<Partial<T>>[]
      );

      if (!isValid) return;
    }

    setActiveStep(stepIndex);

    setStepCompleted((prev) => {
      const newCompleted = [...prev];
      newCompleted[activeStep] = true;
      return newCompleted;
    });
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'fit-content',
        height: '100%',
        backgroundColor: theme.palette.semantic.surface.bg3,
        py: 2,
        px: 3,
        borderRadius: '4px',
      }}
    >
      <Stepper
        nonLinear
        activeStep={activeStep}
        orientation="vertical"
        connector={
          <StepConnector
            sx={{
              ml: '9px',
              position: 'relative',

              '& .MuiStepConnector-line': {
                minHeight: 18,
                border: 0,
                width: '1px',
                backgroundColor: theme.palette.semantic.stepper.background,
              },
            }}
          />
        }
        sx={{
          '& .MuiStep-root': {
            p: 0,
          },

          '& .MuiStepLabel-root': {
            p: 0,
            minHeight: 28,
            alignItems: 'center',
          },

          '& .MuiStepLabel-iconContainer': {
            width: 20,
            height: 20,
            p: 0,
            mr: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },

          '& .MuiStepLabel-labelContainer': {
            minWidth: 0,
          },

          '& .MuiStepLabel-label': {
            margin: 0,
            mt: 0.2,
            fontSize: 14,
            lineHeight: 1.4,
            fontWeight: 400,
            color: theme.palette.semantic.stepper.background,
          },

          '& .MuiStepLabel-label.Mui-completed': {
            color: theme.palette.semantic.stepper.background,
          },

          '& .MuiStepLabel-label.Mui-active': {
            fontWeight: 600,
            color: theme.palette.semantic.stepper.active,
          },
          '& .MuiStepLabel-label.Mui-error': {
            fontWeight: 400,
            color: theme.palette.semantic.stepper.warning,
          },
          '& .MuiStepLabel-label.Mui-active.Mui-error': {
            fontWeight: 600,
            color: theme.palette.semantic.stepper.warning,
          },

          '& .MuiStepContent-root': {
            ml: '9px',
            pl: '20px',
            borderLeft: 'none',
          },
        }}
      >
        {steps.map((step, index) => {
          const hasError =
            step.schema &&
            stepHasError(step.schema as ZodObject<ZodRawShape>, errors);
          const stepSubStep =
            subStep?.filter((s) => s.stepIndex === step.label) ?? [];
          const error = Boolean(hasError);
          return (
            <Step
              key={step.id ?? step.label}
              completed={stepCompleted[index]}
              onClick={() =>
                !handleStepClick ? onStepClick(index) : handleStepClick(index)
              }
              sx={{
                position: 'relative',
                cursor: stepCompleted[index] ? 'pointer' : 'default',
                pointerEvents: stepCompleted[index] ? 'auto' : 'none',
                my: 1,

                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: '9px',
                  top: '30px',
                  bottom: '-16px',
                  width: '1px',
                  backgroundColor: theme.palette.semantic.stepper.background,
                },

                '&:last-child::after': {
                  display: 'none',
                },
              }}
            >
              <StepLabel StepIconComponent={CustomStepIcon} error={error}>
                {step.label}
              </StepLabel>

              {stepSubStep.length > 0 && (
                <StepContent>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {stepSubStep
                      .slice()
                      .reverse()
                      .map((subDetail) => {
                        const isWarning = subDetail.hasError ?? false;

                        return (
                          <Typography
                            key={subDetail.title}
                            variant="body2"
                            sx={{
                              color: isWarning
                                ? theme.palette.semantic.stepper.warning
                                : theme.palette.semantic.stepper.background,
                            }}
                          >
                            {subDetail.title}
                          </Typography>
                        );
                      })}
                  </Stack>
                </StepContent>
              )}
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
}
